// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { TenantIndustry } from 'apis/tenants';
import { TenantCreateEditModalType } from 'components/organisms/AdminTenantManagement';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FloorItem, InputTenantItem, TenantForm } from 'util/Types';
import {
  validateEmailAddress,
  validateExactLength,
  validatePhoneNumber,
  validatePostCodeForm,
  validateRequiredField,
  validateId,
  validateStringField,
  validateMaxLength,
} from 'util/validator';
import { InputDataListField } from '../InputDataListField';
import { SelectField } from '../SelectField';
import styles from './CreateUpdateTenantInputForm.module.scss';
import { Label } from 'components/atoms/Label';
import Toggle from 'components/atoms/Toggle';
import { REGEX_OBJECT } from 'util/ConstantValues';
import { ModalType } from 'util/Enums';
import PostalCodeForm from '../PostalCodeForm';
import { fetchProvinceDistrictSuggestion } from 'apis/operator_site/siteApi';
import { uniqBy } from 'lodash';
import { SelectFieldMultipleChoise } from '../SelectFieldMultipleChoise';

interface Props {
  initValues: TenantForm;
  sectionList: { label: string; value: number }[];
  createUpdateModal: TenantCreateEditModalType;
  brandList: { label: string; value: string }[];
  floorList: FloorItem[];
  tenantIndustries: TenantIndustry[];
  onNextStep: (tenantForm: TenantForm) => void;
  isSubEmissionSite?: boolean;
  siteWasteUnits: InputTenantItem[];
}

const CreateUpdateTenantInputForm: React.FC<Props> = ({
  initValues,
  createUpdateModal,
  sectionList,
  brandList,
  floorList,
  tenantIndustries,
  onNextStep,
  isSubEmissionSite,
  siteWasteUnits,
}) => {
  const { tenantEditing } = createUpdateModal;
  const [floorListState, setFloorListState] = useState<
    { label: string; value: number }[]
  >([]);
  const [isDashboardEnabled, setIsDashboardEnabled] = useState<boolean>(false);
  const { t } = useTranslation();
  const [provinceSuggestion, setProvinceSuggestion] = useState<string[]>([]);
  const [districtSuggestion, setDistrictSuggestion] = useState<string[]>([]);

  useEffect(() => {
    fetchProvinceDistrictSuggestion(false, (data) => {
      if (data) {
        setProvinceSuggestion(uniqBy(data.map((item) => item.province)));
        setDistrictSuggestion(uniqBy(data.map((item) => item.district)));
      }
    });
  }, []);

  const wasteUnitOptions = useMemo(() => {
    return siteWasteUnits.map(({ id, name, category }) => ({
      id,
      name: `${name} - ${category?.name}`,
    }));
  }, [siteWasteUnits]);

  useEffect(() => {
    if (tenantEditing?.section?.id) {
      setFloorListState(
        floorList
          .filter((floor) => floor.sectionId === tenantEditing.section?.id)
          .map((floor) => {
            return { value: floor.id, label: floor.name };
          })
      );
    }
  }, [tenantEditing]);

  useEffect(() => {
    setIsDashboardEnabled(initValues.isDashboardEnabled);
  }, [initValues]);

  const handleChangeSection = (sectionId: string | number) => {
    setFloorListState(
      floorList
        .filter((floor) => floor.sectionId === +sectionId)
        .map((floor) => {
          return { value: floor.id, label: floor.name };
        })
    );
  };

  return (
    <div className={styles.createBody}>
      <Form<TenantForm>
        initialValues={initValues}
        onSubmit={(values) => onNextStep({ ...values, isDashboardEnabled })}
        validate={(values) => {
          return {
            userId: values.userId?.trim()
              ? validateId(t('login.user_id'), 20, values.userId)
              : undefined,
            name: validateStringField('テナント名', 100, values.name),
            industry: validateStringField('テナント業種', 100, values.industry),
            ...(!isSubEmissionSite && {
              contactName: validateStringField(
                t('tenant.thead_contact_name'),
                100,
                values.contactName
              ),
              contactEmail: validateStringField(
                t('tenant.thead_contact_email'),
                100,
                values.contactEmail
              ),
            }),
            ...(values.isEdiEnabled && {
              ediName:
                validateRequiredField(
                  values.ediName,
                  t('tenant.thead_edi_name') || ''
                ) ||
                validateMaxLength(
                  values.ediName,
                  120,
                  t('tenant.thead_edi_name') || ''
                ),
              ediSubscriberNumber:
                validateRequiredField(
                  values.ediSubscriberNumber,
                  t('tenant.thead_edi_subscriber_number') || ''
                ) ||
                validateExactLength({
                  value: values.ediSubscriberNumber,
                  length: 7,
                }),
              ediConfirmationKey:
                validateRequiredField(
                  values.ediConfirmationKey,
                  t('tenant.thead_edi_confirmation_key') || ''
                ) ||
                validateExactLength({
                  value: values.ediConfirmationKey,
                  length: 8,
                }),
              postalCode: validateRequiredField(
                values.postalCode,
                t('tenant.thead_postal_code') || ''
              ),
              staffTel: validatePhoneNumber(
                t('tenant.thead_contact_email'),
                15,
                values.staffTel
              ),
              ...validatePostCodeForm(values, false),
            }),
          };
        }}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
      >
        {(props) => (
          <form className={styles.formInput}>
            <div className={styles.inputFrame}>
              {createUpdateModal.type === ModalType.add &&
                !isSubEmissionSite && (
                  <InputDataListField
                    label={t('login.user_id') || 'ユーザーID'}
                    placeholder={t('login.user_id') || 'ユーザーID'}
                    name="userId"
                    maxlength={20}
                  />
                )}

              <SelectField
                label={t('tenant.thead_section') || 'セクション'}
                name="sectionId"
                placeholder={t('common.select') || '選択する'}
                options={sectionList}
                disabled={!sectionList.length}
                onChange={(value) => handleChangeSection(value)}
              />

              <SelectField
                label={t('tenant.thead_floor') || 'フロア'}
                name="floorId"
                placeholder={t('common.select') || '選択する'}
                options={floorListState}
                disabled={!floorListState.length}
              />

              {!isSubEmissionSite && (
                <SelectField
                  label={t('tenant.thead_brand_owner') || 'ブランドオーナー'}
                  name="brandId"
                  placeholder={t('common.select') || '選択する'}
                  options={brandList}
                  disabled={!brandList.length}
                />
              )}

              <InputDataListField
                label={t('tenant.thead_tenant_name') || 'テナント名'}
                placeholder={t('tenant.thead_tenant_name') || 'テナント名'}
                name="name"
                required
                maxlength={100}
              />

              {!isSubEmissionSite && (
                <InputDataListField
                  label={t('tenant.thead_tenant_number') || 'テナント番号'}
                  placeholder={
                    t('tenant.thead_tenant_number') || 'テナント番号'
                  }
                  name="tenantNumber"
                  maxlength={100}
                />
              )}

              <InputDataListField
                label={t('tenant.thead_tenant_industry') || 'テナント業種'}
                placeholder={
                  t('tenant.thead_tenant_industry') || 'テナント業種'
                }
                name="industry"
                required
                maxlength={100}
                dataSuggestions={
                  tenantIndustries?.map((i) => {
                    return { name: i.industry };
                  }) || []
                }
                onSelectSuggestItem={(value) => {
                  props.form.mutators.setValue('industry', value);
                }}
              />
              <p className={styles.industryDesc}>
                ※テナント業種が分からない場合、「その他」を記入してください。
              </p>

              {!isSubEmissionSite && (
                <>
                  <InputDataListField
                    label={t('tenant.thead_contact_name')}
                    placeholder={t('tenant.thead_contact_name')}
                    name="contactName"
                    required
                    maxlength={100}
                  />
                  <InputDataListField
                    label={t('tenant.thead_contact_email')}
                    placeholder={t('tenant.thead_contact_email')}
                    name="contactEmail"
                    required
                    maxlength={100}
                    validate={validateEmailAddress}
                  />
                  <div className={styles.viewReport}>
                    <Label
                      className={styles.label}
                      text={t('tenant.thead_view_report') || ''}
                      tag={'div'}
                      required
                    />
                    <Toggle
                      value={isDashboardEnabled}
                      setValue={setIsDashboardEnabled}
                    />
                  </div>
                </>
              )}

              {!isSubEmissionSite && (
                <div className={styles.edi}>
                  <Label
                    className={styles.label}
                    text={t('tenant.thead_edi') || ''}
                    tag={'div'}
                  />
                  <Toggle
                    value={props.values.isEdiEnabled}
                    setValue={(value) =>
                      props.form.mutators.setValue('isEdiEnabled', value)
                    }
                  />
                </div>
              )}

              {props.values.isEdiEnabled && !isSubEmissionSite && (
                <>
                  <InputDataListField
                    label={t('tenant.thead_edi_name')}
                    placeholder={t('tenant.thead_edi_name')}
                    name="ediName"
                    required
                    maxlength={120}
                  />
                  <InputDataListField
                    label={t('tenant.thead_edi_subscriber_number')}
                    placeholder={t('tenant.thead_edi_subscriber_number')}
                    name="ediSubscriberNumber"
                    required
                    maxlength={7}
                    validRegex={REGEX_OBJECT.numberStringOnly}
                  />
                  <InputDataListField
                    label={t('tenant.thead_edi_confirmation_key')}
                    placeholder={t('tenant.thead_edi_confirmation_key')}
                    name="ediConfirmationKey"
                    required
                    maxlength={8}
                    validRegex={REGEX_OBJECT.numberStringOnly}
                  />
                  <PostalCodeForm<TenantForm>
                    provinceSuggestions={provinceSuggestion}
                    districtSuggestions={districtSuggestion}
                    formProps={props}
                    mode={createUpdateModal.type}
                    isVertical
                    showLongLat={false}
                    requirePostCode
                  />
                  <InputDataListField
                    label={t('tenant.thead_staff_tel')}
                    placeholder={t('tenant.thead_staff_tel')}
                    name="staffTel"
                    required
                    maxlength={15}
                    validRegex={REGEX_OBJECT.number}
                  />
                </>
              )}

              {!isSubEmissionSite && (
                <SelectFieldMultipleChoise
                  options={wasteUnitOptions}
                  name="hiddenItems"
                  label={t('tenant.thead_hidden_items') || '非表示品目'}
                  placeholder={t('tenant.thead_hidden_items')}
                  onChange={(tags) => {
                    props.form.mutators.setValue(
                      'hiddenItems',
                      tags.map((item) => item.id)
                    );
                  }}
                  selectedOptions={props.values.hiddenItems || []}
                  menuPlacement="top"
                  searchable
                  allowClear
                />
              )}
            </div>

            <div className={styles.line} />

            <div className={styles.submit}>
              <button
                disabled={props.invalid}
                className={
                  props.invalid ? styles.disableButton : styles.submitButton
                }
                onClick={props.handleSubmit}
                type="button"
              >
                {t('common.button.next')}
              </button>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default CreateUpdateTenantInputForm;
